/** Обёртка над fetch: JSON, CSRF-токен, единый формат ошибок API. */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
  /** Сообщения у полей формы из 422 (`details.fields`). */
  get fields(): Record<string, string> {
    const d = this.details as { fields?: Record<string, string> } | undefined;
    return d?.fields ?? {};
  }
}

let csrfToken = '';
export const setCsrf = (t: string) => {
  csrfToken = t;
};
export const getCsrf = () => csrfToken;

type Listener = () => void;
const onUnauthorized = new Set<Listener>();
/** Подписка на «сессия кончилась» — чтобы показать страницу входа. */
export function subscribeUnauthorized(fn: Listener) {
  onUnauthorized.add(fn);
  return () => {
    onUnauthorized.delete(fn);
  };
}

export async function api<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: string,
  body?: unknown,
  opts: { signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;
  let res: Response;
  try {
    res = await fetch(`/api${url}`, {
      method,
      headers,
      body: payload,
      credentials: 'same-origin',
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'network', 'Нет связи с сервером. Проверьте интернет и повторите.');
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const e = (data as { error?: { code: string; message: string; details?: unknown } } | null)?.error;
    const err = new ApiError(
      res.status,
      e?.code ?? 'internal',
      e?.message ?? `Ошибка ${res.status}`,
      e?.details,
    );
    if (res.status === 401 && !url.startsWith('/auth/')) onUnauthorized.forEach((f) => f());
    throw err;
  }
  return data as T;
}

export const get = <T>(url: string, signal?: AbortSignal) => api<T>('GET', url, undefined, { signal });
export const post = <T>(url: string, body?: unknown) => api<T>('POST', url, body ?? {});
export const put = <T>(url: string, body: unknown) => api<T>('PUT', url, body);
export const patch = <T>(url: string, body: unknown) => api<T>('PATCH', url, body);
export const del = <T>(url: string) => api<T>('DELETE', url);

/** Строка запроса без пустых значений. */
export function qs(params: Record<string, string | undefined | null | false>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : '';
}
